// Pure helpers for the conversion-sheet grid (SPEC-068). No React, no DOM.

// Backend header keys are camelCase; snake_case is accepted for older payloads.
const toCamel = (key) => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())

const NUMERIC_COLUMNS = new Set(["leadTimeToLatestArrival", "leadTimeToDeadline", "leadTimeToEarliestStart"])
const REQUIRED_COLUMNS = new Set(["limsAnalysisName", "plannertechAnalysisName", "leadTimeToDeadline", "projectTemplateName"])
// The LIMS columns that identify an incoming row are shown first, in this order.
const LIMS_EXTERNAL_ORDER = ["limsAnalysisName", "limsMaterialNumber", "limsProductDescription", "limsCustomerName"]

export const isNumericColumn = (key) => NUMERIC_COLUMNS.has(toCamel(key))
export const isRequiredColumn = (key) => REQUIRED_COLUMNS.has(toCamel(key))

export function orderHeaders(headers, sheetType) {
  if (sheetType !== "Sample") return headers
  const rank = (header) => {
    const i = LIMS_EXTERNAL_ORDER.indexOf(toCamel(header.key))
    return i === -1 ? LIMS_EXTERNAL_ORDER.length : i
  }
  // Array.prototype.sort is stable, so the remaining columns keep the backend's order.
  return [...headers].sort((a, b) => rank(a) - rank(b))
}

export const isEmptyValue = (value) => value == null || String(value).trim() === ""

// Text shown in a cell. 0 and false are values, not blanks.
export const cellText = (value) => (value == null ? "" : String(value))

export function isCellValid(key, value) {
  if (isEmptyValue(value)) return !isRequiredColumn(key)
  if (!isNumericColumn(key)) return true
  const number = Number(value)
  return Number.isFinite(number) && number >= 0
}

// Numeric input accepts a decimal comma and ignores stray characters such as a " days" suffix.
// A minus sign is kept so a negative value is flagged instead of silently turning positive.
const normaliseNumeric = (text) => text.replace(/,/g, ".").replace(/[^\d.-]/g, "")

// Returns a copy of `row` with the user's text for `key` applied. An empty optional cell is
// removed from the row; an empty required cell is kept as "" so validation can flag it.
export function applyCellText(row, key, rawText) {
  let text = rawText.trim()
  if (isNumericColumn(key)) text = normaliseNumeric(text)
  const next = { ...row }
  if (text === "" && !isRequiredColumn(key)) {
    delete next[key]
  } else if (isNumericColumn(key) && text !== "" && isCellValid(key, text)) {
    next[key] = parseFloat(text)
  } else {
    next[key] = text
  }
  return next
}

// eslint-disable-next-line no-control-regex
const LINE_BREAK_OR_CONTROL = /[\x00-\x1F\x7F-\x9F\u2028\u2029]/g

// Reads a quoted cell starting after its opening quote. Returns the raw cell text (doubled
// quotes unescaped) and the index after the closing quote.
function readQuoted(source, from) {
  let text = ""
  let i = from
  while (i < source.length) {
    const close = source.indexOf('"', i)
    if (close === -1) return [text + source.slice(i), source.length]
    text += source.slice(i, close)
    if (source[close + 1] !== '"') return [text, close + 1]
    text += '"'
    i = close + 2
  }
  return [text, i]
}

// Splits clipboard text (tab-separated, as copied from Excel) into rows of cells. Excel wraps a
// cell containing line breaks or tabs in double quotes; such a cell stays one cell.
export function parseClipboard(text) {
  let source = text.replace(/\r\n?/g, "\n")
  while (source.endsWith("\n")) source = source.slice(0, -1)
  const rows = [[]]
  let cell = ""
  let i = 0
  while (i < source.length) {
    const ch = source[i]
    if (ch === '"' && cell.trim() === "") {
      const [quoted, next] = readQuoted(source, i + 1)
      cell = quoted.replace(LINE_BREAK_OR_CONTROL, " ")
      i = next
      continue
    }
    if (ch === "\t" || ch === "\n") {
      rows[rows.length - 1].push(cell)
      if (ch === "\n") rows.push([])
      cell = ""
    } else {
      cell += ch
    }
    i++
  }
  rows[rows.length - 1].push(cell)
  return rows
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

// Blank cells sort last in both directions; numbers compare numerically, text naturally.
export function compareValues(a, b) {
  const aEmpty = isEmptyValue(a)
  const bEmpty = isEmptyValue(b)
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === "number" && typeof b === "number") return a - b
  return collator.compare(String(a), String(b))
}

// Saved rows sorted by `sort` (stable), followed by new rows in the order they were added.
export function displayOrder(rows, sort) {
  const saved = rows.filter((row) => !row.isNew)
  const added = rows.filter((row) => row.isNew)
  if (sort) {
    const direction = sort.desc ? -1 : 1
    saved.sort((a, b) => {
      const result = compareValues(a.data[sort.key], b.data[sort.key])
      // Blanks stay last regardless of direction.
      return isEmptyValue(a.data[sort.key]) || isEmptyValue(b.data[sort.key]) ? result : result * direction
    })
  }
  return [...saved, ...added]
}

// Header click cycles ascending → descending → unsorted.
export function nextSort(sort, key) {
  if (!sort || sort.key !== key) return { key, desc: false }
  return sort.desc ? null : { key, desc: true }
}

export const exportRows = (headers, rows) => [
  headers.map((header) => header.name),
  ...rows.map((row) => headers.map((header) => row.data[header.key] ?? "")),
]
